import ConfirmationDialog from 'components/Dialog/ConfirmationDialog';
import React, { createContext, useContext, useState, ReactNode, useCallback } from 'react';
// import ReactDOM from 'react-dom';

export interface ConfirmationOptions {
  title?: string;
  message?: string;
  iconClass?: string;
  confirmLabel?: string;
  cancelLabel?: string;
}

interface ConfirmationContextType {
  confirm: (options: ConfirmationOptions, onConfirm?: () => void, onCancel?: () => void) => Promise<boolean>;
}

const ConfirmationContext = createContext<ConfirmationContextType | undefined>(undefined);

export const ConfirmationProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [confirmState, setConfirmState] = useState<{
    options: ConfirmationOptions;
    resolve: (result: boolean) => void;
    onConfirm?: () => void;
    onCancel?: () => void;
  } | null>(null);

  const confirm = useCallback(
    (options: ConfirmationOptions, onConfirm?: () => void, onCancel?: () => void) => {
      return new Promise<boolean>((resolve) => {
        setConfirmState({ options, resolve, onConfirm, onCancel });
      });
    },
    [],
  );

  const handleConfirm = () => {
    if (confirmState) {
      confirmState.resolve(true);
      confirmState.onConfirm && confirmState.onConfirm();
      setConfirmState(null);
    }
  };

  const handleCancel = () => {
    if (confirmState) {
      confirmState.resolve(false);
      confirmState.onCancel && confirmState.onCancel();
      setConfirmState(null);
    }
  };

  return (
    <ConfirmationContext.Provider value={{ confirm }}>
      {children}
      {confirmState && (
        <ConfirmationDialog {...confirmState.options} onConfirm={handleConfirm} onCancel={handleCancel} />
      )}
    </ConfirmationContext.Provider>
  );
};

export const useConfirmation = () => {
  const context = useContext(ConfirmationContext);
  if (!context) {
    throw new Error('useConfirmation must be used within a ConfirmationProvider');
  }
  return context.confirm;
};
